import { BillOfMaterialsLineItem, BillOfMaterialsLineItemType, ComplaintLog, ComplaintLogProduct, ComplaintLogStatus, PlasticsFinishingStation, PlasticsFinishingTask, PlasticsFinishingTaskStatus, PlasticsMoldingTask, PlasticsMoldingTaskStatus, ProductType, ProductVersion, RotoSchedule } from '@prism/prism-shared';

export type ScheduledComplaintProductSource = 'direct' | 'billOfMaterials';

export interface ScheduledComplaintProductBaseMatch {
    complaintLog: ComplaintLog;
    complaintLogProduct: ComplaintLogProduct;
    productId: string;
    productVersion: ProductVersion | null;
    source: ScheduledComplaintProductSource;
    sourceProductId: string;
    sourceProductVersion: ProductVersion | null;
    billOfMaterialsLineItem: BillOfMaterialsLineItem | null;
}

export interface ScheduledComplaintMoldingTaskMatch extends ScheduledComplaintProductBaseMatch {
    task: PlasticsMoldingTask;
    productListId: string;
    machineId: string;
    armId: string;
    scheduleIndex: number;
}

export interface ScheduledComplaintFinishingTaskMatch extends ScheduledComplaintProductBaseMatch {
    task: PlasticsFinishingTask;
    station: PlasticsFinishingStation;
    scheduleIndex: number;
}

export interface ScheduledComplaintProductMatches {
    molding: ScheduledComplaintMoldingTaskMatch[];
    finishing: ScheduledComplaintFinishingTaskMatch[];
    moldingTaskIds: string[];
    finishingTaskIds: string[];
}

export interface ScheduledComplaintProductMatchInput {
    complaintLogs: ComplaintLog[];
    complaintLogProducts: ComplaintLogProduct[];
    productVersions: ProductVersion[];
    billOfMaterialsLineItems: BillOfMaterialsLineItem[];
    rotoSchedule: RotoSchedule | null;
    moldingTasks: PlasticsMoldingTask[];
    finishingStations: PlasticsFinishingStation[];
    finishingTasks: PlasticsFinishingTask[];
}

export function toScheduledComplaintProductMatches(
    input: ScheduledComplaintProductMatchInput,
): ScheduledComplaintProductMatches {
    const productVersionByProductId = input.productVersions.reduce((acc, productVersion) => {
        acc[productVersion.productId] = productVersion;
        return acc;
    }, {} as Record<string, ProductVersion>);

    const complaintLogById = input.complaintLogs
        .filter(complaintLog => (complaintLog.status || ComplaintLogStatus.Unresolved) === ComplaintLogStatus.Unresolved)
        .reduce((acc, complaintLog) => {
            acc[complaintLog.id] = complaintLog;
            return acc;
        }, {} as Record<string, ComplaintLog>);

    const complaintProducts = input.complaintLogProducts
        .filter(complaintLogProduct => !!complaintLogById[complaintLogProduct.complaintLogId]);

    const billOfMaterialsByProductVersionId = input.billOfMaterialsLineItems.reduce((acc, lineItem) => {
        if (!acc[lineItem.productVersionId]) {
            acc[lineItem.productVersionId] = [];
        }
        acc[lineItem.productVersionId].push(lineItem);
        return acc;
    }, {} as Record<string, BillOfMaterialsLineItem[]>);

    const moldingWatchlist = new Map<string, ScheduledComplaintProductBaseMatch[]>();
    const finishingWatchlist = new Map<string, ScheduledComplaintProductBaseMatch[]>();

    const addWatch = (
        watchlist: Map<string, ScheduledComplaintProductBaseMatch[]>,
        productId: string,
        match: ScheduledComplaintProductBaseMatch,
    ) => {
        if (!watchlist.has(productId)) {
            watchlist.set(productId, []);
        }
        watchlist.get(productId)!.push(match);
    };

    complaintProducts.forEach((complaintLogProduct) => {
        const complaintLog = complaintLogById[complaintLogProduct.complaintLogId];
        if (!complaintLog) return;

        const linkedProductVersion = productVersionByProductId[complaintLogProduct.productId] || null;
        const directMatch: ScheduledComplaintProductBaseMatch = {
            complaintLog,
            complaintLogProduct,
            productId: complaintLogProduct.productId,
            productVersion: linkedProductVersion,
            source: 'direct',
            sourceProductId: complaintLogProduct.productId,
            sourceProductVersion: linkedProductVersion,
            billOfMaterialsLineItem: null,
        };

        addWatch(finishingWatchlist, complaintLogProduct.productId, directMatch);

        if (linkedProductVersion?.type === ProductType.AS_MOLDED) {
            addWatch(moldingWatchlist, complaintLogProduct.productId, directMatch);
        }

        if (!linkedProductVersion) return;

        const lineItems = billOfMaterialsByProductVersionId[linkedProductVersion.id] || [];
        lineItems
            .filter(lineItem => lineItem.type === BillOfMaterialsLineItemType.Product)
            .forEach((lineItem) => {
                const bomProductVersion = productVersionByProductId[lineItem.entityId] || null;
                if (bomProductVersion?.type !== ProductType.AS_MOLDED) return;

                addWatch(moldingWatchlist, lineItem.entityId, {
                    complaintLog,
                    complaintLogProduct,
                    productId: lineItem.entityId,
                    productVersion: bomProductVersion,
                    source: 'billOfMaterials',
                    sourceProductId: complaintLogProduct.productId,
                    sourceProductVersion: linkedProductVersion,
                    billOfMaterialsLineItem: lineItem,
                });
            });
    });

    const moldingTaskById = input.moldingTasks.reduce((acc, task) => {
        acc[task.id] = task;
        return acc;
    }, {} as Record<string, PlasticsMoldingTask>);

    const molding: ScheduledComplaintMoldingTaskMatch[] = [];
    (input.rotoSchedule?.productLists || []).forEach((productList) => {
        productList.moldingTaskIds.forEach((moldingTaskId, scheduleIndex) => {
            const task = moldingTaskById[moldingTaskId];
            if (!task || task.status !== PlasticsMoldingTaskStatus.IN_PROGRESS) return;

            const matches = moldingWatchlist.get(task.productId) || [];
            matches.forEach(match => molding.push({
                ...match,
                task,
                productListId: productList.id,
                machineId: productList.machineId,
                armId: productList.armId,
                scheduleIndex,
            }));
        });
    });

    const finishingTaskById = input.finishingTasks.reduce((acc, task) => {
        acc[task.id] = task;
        return acc;
    }, {} as Record<string, PlasticsFinishingTask>);

    const finishing: ScheduledComplaintFinishingTaskMatch[] = [];
    input.finishingStations.forEach((station) => {
        const visibleTaskIds = station.hideTasksFromIndex >= 0
            ? station.taskIds.slice(0, station.hideTasksFromIndex)
            : station.taskIds;

        visibleTaskIds.forEach((finishingTaskId, scheduleIndex) => {
            const task = finishingTaskById[finishingTaskId];
            if (!task || task.status !== PlasticsFinishingTaskStatus.InProgress) return;

            const matches = finishingWatchlist.get(task.productId) || [];
            matches.forEach(match => finishing.push({
                ...match,
                task,
                station,
                scheduleIndex,
            }));
        });
    });

    return {
        molding,
        finishing,
        moldingTaskIds: [...new Set(molding.map(match => match.task.id))],
        finishingTaskIds: [...new Set(finishing.map(match => match.task.id))],
    };
}
